<template>
  <Teleport to="body">
    <Transition name="modal-fade">
      <div v-if="show" class="modal-overlay" @click.self="closeModal">
        <Transition name="modal-slide">
          <div v-if="show" class="declaration-modal">
            <!-- Header avec animation -->
            <div class="modal-header" :class="declarationType">
              <div class="header-icon-wrapper">
                <div class="icon-pulse"></div>
                <div class="header-icon">
                  <i :class="declarationType === 'deteriore' ? 'fas fa-tools' : 'fas fa-exclamation-triangle'"></i>
                </div>
              </div>
              <div class="header-content">
                <h2>{{ declarationType === 'deteriore' ? '🔧 Déclaration de Détérioration' : '🚨 Déclaration de Vol' }}</h2>
                <p>{{ declarationType === 'deteriore' ? 'Signalez un appareil endommagé ou défectueux' : 'Signalez un appareil volé ou disparu' }}</p>
              </div>
              <button @click="closeModal" class="close-btn">
                <i class="fas fa-times"></i>
              </button>
            </div>

            <!-- Corps du modal -->
            <div class="modal-body">
              <!-- Sélection du type de déclaration -->
              <div class="declaration-type-selector">
                <div 
                  class="type-card" 
                  :class="{ active: declarationType === 'deteriore' }"
                  @click="declarationType = 'deteriore'"
                >
                  <div class="type-icon deteriore">
                    <i class="fas fa-tools"></i>
                  </div>
                  <div class="type-info">
                    <h4>Défectueux</h4>
                    <p>Appareil endommagé ou défectueux</p>
                  </div>
                  <div class="type-check" v-if="declarationType === 'deteriore'">
                    <i class="fas fa-check-circle"></i>
                  </div>
                </div>
                
                <div 
                  class="type-card" 
                  :class="{ active: declarationType === 'vole' }"
                  @click="declarationType = 'vole'"
                >
                  <div class="type-icon vole">
                    <i class="fas fa-exclamation-triangle"></i>
                  </div>
                  <div class="type-info">
                    <h4>Perdu</h4>
                    <p>Appareil perdu ou disparu</p>
                  </div>
                  <div class="type-check" v-if="declarationType === 'vole'">
                    <i class="fas fa-check-circle"></i>
                  </div>
                </div>
              </div>

              <!-- Informations du produit -->
              <div class="product-info-section" v-if="product">
                <h3><i class="fas fa-box"></i> Appareil concerné</h3>
                <div class="product-card-info">
                  <div class="product-image">
                    <i class="fas fa-microchip"></i>
                  </div>
                  <div class="product-details">
                    <div class="product-name">{{ product.designation }}</div>
                    <div class="product-ref">Réf: {{ product.referenceInterne }}</div>
                    <div class="product-identifiers">
                      <span v-if="product.imei" class="identifier">
                        <i class="fas fa-mobile-alt"></i> IMEI: {{ product.imei }}
                      </span>
                      <span v-if="product.iccid" class="identifier">
                        <i class="fas fa-sim-card"></i> ICCID: {{ product.iccid }}
                      </span>
                    </div>
                    <div class="product-category">
                      <span class="category-badge">{{ product.categorie }}</span>
                      <span class="status-badge" :class="product.statut">{{ getStatusLabel(product.statut) }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Formulaire de déclaration -->
              <div class="declaration-form">
                <h3><i class="fas fa-clipboard-list"></i> Détails de la déclaration</h3>
                
                <div class="form-group">
                  <label>
                    <i class="fas fa-calendar-alt"></i>
                    Date de l'incident *
                  </label>
                  <input 
                    type="date" 
                    v-model="formData.dateIncident" 
                    class="form-input"
                    :max="today"
                    required
                  >
                </div>

                <div class="form-group" v-if="declarationType === 'deteriore'">
                  <label>
                    <i class="fas fa-exclamation-circle"></i>
                    Type de dommage *
                  </label>
                  <select v-model="formData.typeDommage" class="form-input" required>
                    <option value="">Sélectionner le type de dommage</option>
                    <option value="ecran_casse">Écran cassé</option>
                    <option value="boitier_endommage">Boîtier endommagé</option>
                    <option value="connecteur_defaillant">Connecteur défaillant</option>
                    <option value="batterie_hs">Batterie HS</option>
                    <option value="carte_mere">Carte mère défectueuse</option>
                    <option value="eau">Dégât des eaux</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>

                <div class="form-group" v-if="declarationType === 'vole'">
                  <label>
                    <i class="fas fa-map-marker-alt"></i>
                    Lieu du vol *
                  </label>
                  <input 
                    type="text" 
                    v-model="formData.lieuVol" 
                    class="form-input"
                    placeholder="Ex: Véhicule, Chantier, Bureau..."
                    required
                  >
                </div>

                <div class="form-group" v-if="declarationType === 'vole'">
                  <label>
                    <i class="fas fa-file-alt"></i>
                    Numéro de dépôt de plainte
                  </label>
                  <input 
                    type="text" 
                    v-model="formData.numeroPlainte" 
                    class="form-input"
                    placeholder="Numéro de la plainte déposée (optionnel)"
                  >
                </div>

                <div class="form-group">
                  <label>
                    <i class="fas fa-comment-alt"></i>
                    Description détaillée *
                  </label>
                  <textarea 
                    v-model="formData.description" 
                    class="form-input textarea"
                    placeholder="Décrivez les circonstances de l'incident..."
                    rows="4"
                    required
                  ></textarea>
                </div>

                <div class="form-group">
                  <label>
                    <i class="fas fa-camera"></i>
                    Photos justificatives
                  </label>
                  <div class="photo-upload-zone" @click="triggerFileUpload">
                    <input 
                      ref="fileInput"
                      type="file" 
                      accept="image/*"
                      multiple
                      @change="handleFileUpload"
                      style="display: none"
                    >
                    <div class="upload-content">
                      <i class="fas fa-cloud-upload-alt"></i>
                      <p>Cliquez pour ajouter des photos</p>
                      <small>JPG, PNG - Max 5MB par fichier</small>
                    </div>
                  </div>
                  <div v-if="formData.photos.length > 0" class="photos-preview">
                    <div v-for="(photo, index) in formData.photos" :key="index" class="photo-item">
                      <img :src="photo" alt="Photo justificative">
                      <button @click="removePhoto(index)" class="remove-photo">
                        <i class="fas fa-times"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Footer -->
            <div class="modal-footer">
              <button @click="closeModal" class="btn btn-secondary">
                <i class="fas fa-times"></i>
                Annuler
              </button>
              <button 
                @click="submitDeclaration" 
                class="btn btn-primary"
                :class="declarationType"
                :disabled="!isFormValid || isSubmitting"
              >
                <i v-if="isSubmitting" class="fas fa-spinner fa-spin"></i>
                <i v-else :class="declarationType === 'deteriore' ? 'fas fa-tools' : 'fas fa-exclamation-triangle'"></i>
                {{ isSubmitting ? 'Envoi en cours...' : 'Soumettre la déclaration' }}
              </button>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { useToast } from 'vue-toastification'

export default {
  name: 'DeclarationIncidentModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    product: {
      type: Object,
      default: null
    },
    userRole: {
      type: String,
      default: ''
    }
  },
  emits: ['close', 'declaration-submitted'],
  setup(props, { emit }) {
    const toast = useToast()
    const fileInput = ref(null)
    const isSubmitting = ref(false)
    const declarationType = ref('deteriore')
    
    const today = computed(() => new Date().toISOString().split('T')[0])
    
    const formData = ref({
      dateIncident: today.value,
      typeDommage: '',
      lieuVol: '',
      numeroPlainte: '',
      description: '',
      photos: []
    })

    const isFormValid = computed(() => {
      if (!formData.value.dateIncident || !formData.value.description) return false
      if (declarationType.value === 'deteriore' && !formData.value.typeDommage) return false
      if (declarationType.value === 'vole' && !formData.value.lieuVol) return false
      return true
    })

    const getStatusLabel = (statut) => {
      const labels = {
        'en_stock': 'En stock',
        'affecte_technicien': 'Affecté technicien',
        'en_cours': 'En cours',
        'installe': 'Installé',
        'retourne': 'Retourné',
        'defectueux': 'Défectueux',
        'perdu': 'Perdu',
        'deteriore': 'Détérioré',
        'vole': 'Volé'
      }
      return labels[statut] || statut
    }

    const closeModal = () => {
      emit('close')
      resetForm()
    }

    const resetForm = () => {
      formData.value = {
        dateIncident: today.value,
        typeDommage: '',
        lieuVol: '',
        numeroPlainte: '',
        description: '',
        photos: []
      }
      declarationType.value = 'deteriore'
    }

    const triggerFileUpload = () => {
      fileInput.value?.click()
    }

    const handleFileUpload = (event) => {
      const files = Array.from(event.target.files)
      files.forEach(file => {
        if (file.size > 5 * 1024 * 1024) {
          toast.error('Le fichier est trop volumineux (max 5MB)')
          return
        }
        const reader = new FileReader()
        reader.onload = (e) => {
          formData.value.photos.push(e.target.result)
        }
        reader.readAsDataURL(file)
      })
    }

    const removePhoto = (index) => {
      formData.value.photos.splice(index, 1)
    }

    const submitDeclaration = async () => {
      if (!isFormValid.value) {
        toast.error('Veuillez remplir tous les champs obligatoires')
        return
      }

      isSubmitting.value = true

      try {
        const token = localStorage.getItem('token')
        const payload = {
          productId: props.product._id,
          type: declarationType.value,
          dateIncident: formData.value.dateIncident,
          description: formData.value.description,
          photos: formData.value.photos,
          ...(declarationType.value === 'deteriore' && { typeDommage: formData.value.typeDommage }),
          ...(declarationType.value === 'vole' && { 
            lieuVol: formData.value.lieuVol,
            numeroPlainte: formData.value.numeroPlainte
          })
        }

        const response = await fetch('/api/stock/declaration-incident', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        })

        if (response.ok) {
          const result = await response.json()
          toast.success(`✅ Déclaration de ${declarationType.value === 'deteriore' ? 'détérioration' : 'vol'} soumise avec succès !`)
          emit('declaration-submitted', result)
          closeModal()
        } else {
          const error = await response.json()
          toast.error(error.message || 'Erreur lors de la soumission')
        }
      } catch (error) {
        console.error('Erreur:', error)
        toast.error('Erreur lors de la soumission de la déclaration')
      } finally {
        isSubmitting.value = false
      }
    }

    watch(() => props.show, (newVal) => {
      if (newVal) {
        formData.value.dateIncident = today.value
      }
    })

    return {
      fileInput,
      isSubmitting,
      declarationType,
      formData,
      today,
      isFormValid,
      getStatusLabel,
      closeModal,
      triggerFileUpload,
      handleFileUpload,
      removePhoto,
      submitDeclaration
    }
  }
}
</script>

<style scoped>
/* Animations */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-slide-enter-active {
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.modal-slide-leave-active {
  transition: all 0.3s ease-in;
}
.modal-slide-enter-from {
  opacity: 0;
  transform: scale(0.9) translateY(20px);
}
.modal-slide-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(-10px);
}

/* Overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

/* Modal Container */
.declaration-modal {
  background: white;
  border-radius: 24px;
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
}

/* Header */
.modal-header {
  padding: 24px 28px;
  display: flex;
  align-items: center;
  gap: 16px;
  position: relative;
  overflow: hidden;
}

.modal-header.deteriore {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

.modal-header.vole {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}

.header-icon-wrapper {
  position: relative;
  width: 60px;
  height: 60px;
}

.icon-pulse {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { transform: scale(1); opacity: 0.5; }
  50% { transform: scale(1.2); opacity: 0; }
  100% { transform: scale(1); opacity: 0.5; }
}

.header-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 50px;
  height: 50px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
}

.header-content {
  flex: 1;
}

.header-content h2 {
  color: white;
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0 0 4px 0;
}

.header-content p {
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  margin: 0;
}

.close-btn {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: rotate(90deg);
}

/* Body */
.modal-body {
  padding: 24px 28px;
  overflow-y: auto;
  flex: 1;
}

/* Type Selector */
.declaration-type-selector {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 24px;
}

.type-card {
  padding: 20px;
  border: 2px solid #e5e7eb;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 16px;
  position: relative;
}

.type-card:hover {
  border-color: #d1d5db;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.type-card.active {
  border-color: transparent;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
}

.type-card.active:has(.type-icon.deteriore) {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border-color: #f59e0b;
}

.type-card.active:has(.type-icon.vole) {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  border-color: #ef4444;
}

.type-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
}

.type-icon.deteriore {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
}

.type-icon.vole {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
}

.type-info h4 {
  margin: 0 0 4px 0;
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
}

.type-info p {
  margin: 0;
  font-size: 0.8rem;
  color: #6b7280;
}

.type-check {
  position: absolute;
  top: 12px;
  right: 12px;
  color: #10b981;
  font-size: 20px;
  animation: checkBounce 0.4s ease;
}

@keyframes checkBounce {
  0% { transform: scale(0); }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); }
}

/* Product Info Section */
.product-info-section {
  margin-bottom: 24px;
}

.product-info-section h3 {
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.product-card-info {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 16px;
  padding: 20px;
  display: flex;
  gap: 16px;
  border: 1px solid #e2e8f0;
}

.product-image {
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 28px;
}

.product-details {
  flex: 1;
}

.product-name {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 4px;
}

.product-ref {
  font-size: 0.85rem;
  color: #6b7280;
  margin-bottom: 8px;
}

.product-identifiers {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 8px;
}

.product-identifiers .identifier {
  font-size: 0.8rem;
  color: #4b5563;
  display: flex;
  align-items: center;
  gap: 4px;
}

.product-category {
  display: flex;
  gap: 8px;
}

.category-badge {
  padding: 4px 10px;
  background: #dbeafe;
  color: #1d4ed8;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
}

.status-badge {
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
}

.status-badge.en_stock { background: #d1fae5; color: #065f46; }
.status-badge.affecte_technicien { background: #dbeafe; color: #1d4ed8; }
.status-badge.installe { background: #e0e7ff; color: #4338ca; }

/* Form */
.declaration-form h3 {
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
}

.form-input {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 0.95rem;
  transition: all 0.2s;
  background: white;
}

.form-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
}

.form-input.textarea {
  resize: vertical;
  min-height: 100px;
}

/* Photo Upload */
.photo-upload-zone {
  border: 2px dashed #d1d5db;
  border-radius: 16px;
  padding: 30px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  background: #f9fafb;
}

.photo-upload-zone:hover {
  border-color: #3b82f6;
  background: #eff6ff;
}

.upload-content i {
  font-size: 40px;
  color: #9ca3af;
  margin-bottom: 12px;
}

.upload-content p {
  margin: 0 0 4px 0;
  color: #4b5563;
  font-weight: 500;
}

.upload-content small {
  color: #9ca3af;
}

.photos-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 16px;
}

.photo-item {
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 12px;
  overflow: hidden;
}

.photo-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.remove-photo {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: none;
  background: rgba(239, 68, 68, 0.9);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
}

/* Footer */
.modal-footer {
  padding: 20px 28px;
  background: #f8fafc;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.btn {
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
  border: none;
}

.btn-secondary {
  background: #f3f4f6;
  color: #4b5563;
}

.btn-secondary:hover {
  background: #e5e7eb;
}

.btn-primary {
  color: white;
}

.btn-primary.deteriore {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

.btn-primary.vole {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* Responsive */
@media (max-width: 640px) {
  .declaration-type-selector {
    grid-template-columns: 1fr;
  }
  
  .product-card-info {
    flex-direction: column;
    text-align: center;
  }
  
  .product-image {
    margin: 0 auto;
  }
}
</style>
